// "Estudando com IA" — tutor IA focado num tema, com chat conversacional,
// upload de documentos de apoio (PDF/DOC/TXT) e viewer lateral.
//
// Acesso restrito por feature flag (ai_study_access no DB). Quando o user não
// tem acesso, mostra um placeholder explicando.
//
// Fluxo:
//   1. Aluno chega na tela → vê lista de sessões existentes + botão "Novo estudo"
//   2. Cria nova sessão definindo um TEMA específico (limite de 200 chars)
//   3. Entra no workspace: chat à esquerda, viewer/docs à direita
//   4. Pode anexar PDF/TXT — texto é extraído no client e enviado ao backend
//   5. IA responde restrita ao tema (system prompt resiste a prompt injection)

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeft,
  ArrowLeftRight,
  BookOpen,
  Check,
  ChevronDown,
  Copy,
  Database,
  FileText,
  Globe,
  Loader2,
  RefreshCw,
  MessageSquare,
  MoreVertical,
  Paperclip,
  Pencil,
  PenLine,
  Plus,
  Search,
  Send,
  Share2,
  Sparkles,
  StickyNote,
  Trash2,
  Wand2,
  X
} from "lucide-react";
import {
  api,
  ApiError,
  type AiPersona,
  type AiStudyDocument,
  type AiStudyMessage,
  type AiStudySessionDetail,
  type AiStudySessionRow
} from "./api";
import * as LucideIcons from "lucide-react";
import { AiMarkdownBlock } from "./aiMarkdown";
import { EssayWorkspace } from "./EssayWorkspace";
import { EssayLibraryPicker } from "./EssayLibraryPicker";
import { openConfirm, openPrompt } from "./ConfirmPromptModal";
import { openShareDialog } from "./ShareDialog";
import { flagEnabled } from "@app/ui/lib/feature-flags";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { activeVerbosity, VerbosityChip } from "./AiAssistant";
import { BankCard, banksData, banksPhase, loadBanks } from "./App";
import { bankSearchQuery } from "./BankFolders";
import { CadernoNotesPanel } from "./CadernoNotesPanel";
import { GenerateBankModal } from "./GenerateBankModal";
import { CadernoAnalysisModal } from "./CadernoAnalysisModal";
import { IngestUrlModal } from "./IngestUrlModal";
import { LinkedBanksStrip } from "./LinkedBanksContext";
import type { LinkedBankSummary } from "./api";
import { QuickStudyView } from "./QuickStudy";

type Phase = "loading" | "ready" | "no_access" | "error";

export const activeSessionId = signal<string | null>(null);

/** Deeplink reverso banco→caderno. Quando o aluno clica em "Estudar com IA"
 *  dentro de um banco, escrevemos aqui {bankId, bankName} e navegamos pra
 *  aba do AiStudy; a tela detecta e abre o modal de "Novo caderno" já com o
 *  banco anexado e o tema sugerido. Após criar (ou cancelar), limpa.
 *  [[project-caderno-bank-context]] */
export const pendingNewCadernoFromBank = signal<{
  bankId: string;
  bankName: string;
  /** Versão do banco a usar como contexto: "mc" (default, múltipla escolha)
   *  ou "essay" (dissertativa gerada por IA). Em essay, o tema e a descrição
   *  são prefilled mencionando o modo discursivo. */
  bankVersion?: "mc" | "essay";
} | null>(null);

/** Sessão ativa "rica" — lida pelo Breadcrumb do App.tsx pra mostrar o nome
 *  do caderno/redação que o aluno está dentro. Populado quando o detalhe carrega.
 *  null quando o aluno está na lista (ou fora da seção). */
export const activeAiStudySession = signal<{
  id: string;
  title: string;
  kind: "chat" | "essay";
} | null>(null);

// Cores de badge mapeadas das personas ao tailwind. Mesmo set usado em
// AiAssistant.tsx pra manter consistência visual.
const PERSONA_COLOR_CLASS: Record<AiPersona["color"], string> = {
  emerald: "bg-emerald-500/15 text-emerald-600 ring-emerald-500/30 dark:text-emerald-300",
  sky: "bg-sky-500/15 text-sky-600 ring-sky-500/30 dark:text-sky-300",
  violet: "bg-violet-500/15 text-violet-600 ring-violet-500/30 dark:text-violet-300",
  amber: "bg-amber-500/15 text-amber-600 ring-amber-500/30 dark:text-amber-300",
  rose: "bg-rose-500/15 text-rose-600 ring-rose-500/30 dark:text-rose-300",
  teal: "bg-teal-500/15 text-teal-600 ring-teal-500/30 dark:text-teal-300",
  blue: "bg-blue-500/15 text-blue-600 ring-blue-500/30 dark:text-blue-300",
  indigo: "bg-indigo-500/15 text-indigo-600 ring-indigo-500/30 dark:text-indigo-300"
};

function PersonaBadge({ persona }: { persona: AiPersona }) {
  const IconComp = (LucideIcons as any)[persona.icon] || LucideIcons.Sparkles;
  const colorClass = PERSONA_COLOR_CLASS[persona.color] ?? "bg-primary/15 text-primary ring-primary/30";
  return (
    <div
      class={`grid size-11 shrink-0 place-items-center rounded-md ring-1 ${colorClass}`}
      title={persona.label}
    >
      <IconComp className="size-5" />
    </div>
  );
}

function essayPhaseLabel(p: "setup" | "running" | "paused" | "submitted" | null): string {
  switch (p) {
    case "setup": return "Configurando";
    case "running": return "Em execução";
    case "paused": return "Pausada";
    case "submitted": return "Corrigida";
    default: return "—";
  }
}

/**
 * Paleta visual por tipo de sessão. Mantém a identificação a olho:
 *   - Caderno (chat) → azul céu (sky)
 *   - Redação (essay) → violeta (violet)
 * Cada propriedade é uma classe Tailwind pronta — quando precisar adicionar
 * variantes (gradient, ring etc.) basta acrescentar aqui.
 */
export function kindTheme(kind: "chat" | "essay") {
  if (kind === "essay") {
    return {
      label: "Redação",
      icon: "text-violet-500",
      iconBg: "bg-violet-500/15 text-violet-600 dark:text-violet-300",
      badge: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
      border: "border-violet-500/30",
      hover: "hover:border-violet-500/60 hover:bg-violet-500/5",
      selectedBorder: "border-violet-500",
      selectedBg: "bg-violet-500/10",
      gradientFrom: "from-violet-500/5"
    };
  }
  return {
    label: "Caderno",
    icon: "text-sky-500",
    iconBg: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
    badge: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
    border: "border-sky-500/30",
    hover: "hover:border-sky-500/60 hover:bg-sky-500/5",
    selectedBorder: "border-sky-500",
    selectedBg: "bg-sky-500/10",
    gradientFrom: "from-sky-500/5"
  };
}

export type AiStudyTab = "cadernos" | "quickstudy";
/** Signal exportado pra que o card da home (e outros disparadores externos)
 *  consigam pré-selecionar a aba antes de navegar pra /aistudy. */
export const aiStudyTab = signal<AiStudyTab>("cadernos");

export function AiStudyView() {
  if (activeSessionId.value) {
    return (
      <AiStudyWorkspace
        sessionId={activeSessionId.value}
        onExit={() => {
          activeSessionId.value = null;
          activeAiStudySession.value = null;
        }}
      />
    );
  }
  // Garante limpeza do breadcrumb sempre que voltamos pra lista.
  if (activeAiStudySession.value) activeAiStudySession.value = null;
  const tab = aiStudyTab.value;
  return (
    <div class="space-y-4">
      <div class="inline-flex gap-1 rounded-md border border-border bg-card p-0.5">
        <button
          type="button"
          onClick={() => (aiStudyTab.value = "cadernos")}
          class={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
            tab === "cadernos"
              ? "bg-violet-600 text-white"
              : "text-muted-foreground hover:bg-muted"
          }`}
        >
          Cadernos
        </button>
        <button
          type="button"
          onClick={() => (aiStudyTab.value = "quickstudy")}
          class={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
            tab === "quickstudy"
              ? "bg-violet-600 text-white"
              : "text-muted-foreground hover:bg-muted"
          }`}
        >
          Tira-dúvida
        </button>
      </div>
      {tab === "cadernos" ? (
        <AiStudyList onOpen={(id) => (activeSessionId.value = id)} />
      ) : (
        <QuickStudyView />
      )}
    </div>
  );
}

// ============================================================================
// Lista de sessões + criar nova
// ============================================================================

function AiStudyList({ onOpen }: { onOpen: (id: string) => void }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [sessions, setSessions] = useState<AiStudySessionRow[]>([]);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Qual modo abrir o CreateSessionModal: "chat" (caderno padrão) ou "essay"
  // (redação). 2 botões separados no header → menos cliques + visibilidade.
  const [creatingKind, setCreatingKind] = useState<"chat" | "essay">("chat");
  // Busca client-side: filtra cadernos/redações por título, tema e quem
  // compartilhou. Busca dentro das mensagens fica pra um endpoint dedicado
  // se virar necessário. Compartilhamos o signal com BanksView pra que o
  // mesmo termo busque também nos bancos exibidos na seção "Ou escolha um
  // banco de questões" logo abaixo.
  const q = bankSearchQuery.value;

  async function load() {
    setPhase("loading");
    try {
      const r = await api.listAiStudy();
      setSessions(r.sessions.filter((s) => !s.archivedAt));
      setPhase("ready");
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setPhase("no_access");
        setErrMsg(e.message);
        return;
      }
      setPhase("error");
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Deeplink: se chegamos aqui vindo de "Estudar com IA" dentro de um banco,
  // abre o modal de ESCOLHA — adicionar a caderno existente OU criar novo.
  // Antes (2026-05-23) abria direto o "Criar novo", o que induzia ao erro
  // (aluno saía criando dezenas de cadernos do mesmo banco em vez de
  // adicionar ao caderno corrente). [[project-caderno-bank-context]]
  const [chooseCadernoOpen, setChooseCadernoOpen] = useState(false);
  useEffect(() => {
    if (pendingNewCadernoFromBank.value && phase === "ready") {
      setChooseCadernoOpen(true);
    }
  }, [phase]);

  if (phase === "loading") {
    return (
      <p class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  }
  if (phase === "no_access") {
    return (
      <div class="mx-auto max-w-2xl rounded-lg border border-border bg-card/40 p-8 text-center">
        <Sparkles className="mx-auto mb-3 size-10 text-primary" />
        <h2 class="mb-2 text-lg font-bold">Estudando com IA — acesso restrito</h2>
        <p class="text-sm text-muted-foreground">
          Esta feature ainda está em piloto fechado. Para liberação, fale com o
          admin da plataforma.
        </p>
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        <AlertCircle className="mr-2 inline size-4" />
        {errMsg}
      </div>
    );
  }

  const qLower = q.trim().toLowerCase();
  const matchSession = (s: AiStudySessionRow) => {
    if (!qLower) return true;
    const hay =
      s.title.toLowerCase() +
      " " +
      (s.theme ? s.theme.toLowerCase() : "") +
      " " +
      (s.sharedByName ? s.sharedByName.toLowerCase() : "");
    return hay.includes(qLower);
  };
  const chats = sessions.filter((s) => s.kind === "chat" && matchSession(s));
  const essays = sessions.filter((s) => s.kind === "essay" && matchSession(s));

  return (
    <div class="space-y-4">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-2xl font-bold tracking-tight">Estudando com IA</h1>
          <p class="text-sm text-muted-foreground">
            Crie um estudo focado num tema. A IA fica restrita a ele e você pode
            anexar PDFs como apoio.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setCreatingKind("chat");
              setCreating(true);
            }}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110"
          >
            <Plus className="size-4" /> Novo caderno
          </button>
          <button
            type="button"
            onClick={() => {
              setCreatingKind("essay");
              setCreating(true);
            }}
            class="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-rose-500 to-fuchsia-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:brightness-110"
            title="Praticar redação com tema da banca + correção IA"
          >
            <PenLine className="size-4" /> Nova redação
          </button>
        </div>
      </header>

      {sessions.length > 0 && (
        <div class="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onInput={(e) =>
              (bankSearchQuery.value = (e.target as HTMLInputElement).value)
            }
            placeholder="Buscar estudo (título, tema) ou banco abaixo…"
            aria-label="Buscar estudo ou banco"
            class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary"
          />
          {q && (
            <button
              type="button"
              onClick={() => (bankSearchQuery.value = "")}
              aria-label="Limpar busca"
              title="Limpar busca"
              class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}

      {creating && (
        <CreateSessionModal
          initialKind={creatingKind}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onOpen(id);
          }}
        />
      )}

      {chooseCadernoOpen && pendingNewCadernoFromBank.value && (
        <ChooseCadernoForBankModal
          bankInfo={pendingNewCadernoFromBank.value}
          existingChats={sessions.filter((s) => s.kind === "chat" && !s.archivedAt)}
          onClose={() => {
            setChooseCadernoOpen(false);
            pendingNewCadernoFromBank.value = null;
          }}
          onAttached={(sessionId) => {
            setChooseCadernoOpen(false);
            pendingNewCadernoFromBank.value = null;
            onOpen(sessionId);
          }}
          onChooseNew={() => {
            setChooseCadernoOpen(false);
            // pendingNewCadernoFromBank fica seteado pra CreateSessionModal pré-popular
            setCreating(true);
          }}
        />
      )}

      {sessions.length === 0 ? (
        <div class="rounded-lg border border-dashed border-border px-6 py-12 text-center">
          <MessageSquare className="mx-auto mb-3 size-8 text-muted-foreground" />
          <p class="text-sm text-muted-foreground">
            Nenhum estudo ainda. Clique em <strong>Novo estudo</strong> pra começar.
          </p>
        </div>
      ) : (
        <>
          <SessionSection
            kind="chat"
            title="Cadernos de Estudo"
            desc="Chat com IA restrito a um tema, com apoio de PDFs anexados."
            sessions={chats}
            onOpen={onOpen}
            onChange={load}
            emptyMsg={
              qLower
                ? `Nenhum caderno bate com "${q}".`
                : "Nenhum caderno por aqui. Crie um para conversar com a IA sobre um tema."
            }
          />
          <SessionSection
            kind="essay"
            title="Simulações de Redação"
            desc="Redação com tempo, fragmentos de apoio e correção da IA por banca."
            sessions={essays}
            onOpen={onOpen}
            onChange={load}
            emptyMsg={
              qLower
                ? `Nenhuma redação bate com "${q}".`
                : "Nenhuma redação criada. Comece uma para a IA corrigir."
            }
          />
        </>
      )}

      {/* "Ou escolha um banco de questões" — centraliza tudo numa porta de
          entrada só. Reusa o mesmo BankCard / loader da seção "Bancos de
          questões" do menu, pra não divergir comportamento. */}
      <BancosInlineSection />
    </div>
  );
}

// Lista os bancos de questões inline, no padrão do menu "Bancos de questões".
// O click navega pra mesma rota (handleOpen do BankCard já chama setActiveNav
// e openBank). Funciona como atalho de entrada centralizado no "Estudando com IA".
function BancosInlineSection() {
  // Dispara o load na primeira vez que a seção monta. Se já carregou em
  // outra parte do app, reaproveita o cache; senão, busca silenciosamente.
  useEffect(() => {
    if (banksPhase.value === "idle") void loadBanks({ silent: true });
  }, []);

  const phase = banksPhase.value;
  const q = bankSearchQuery.value.trim().toLowerCase();
  const banks = q
    ? banksData.value.filter((b) => {
        const hay =
          b.name.toLowerCase() +
          " " +
          (b.description ? b.description.toLowerCase() : "") +
          " " +
          (b.sharedByName ? b.sharedByName.toLowerCase() : "");
        return hay.includes(q);
      })
    : banksData.value;

  return (
    <section class="space-y-2 pt-4">
      <header class="flex items-center justify-between gap-3 border-b border-border pb-2">
        <div class="flex items-center gap-2 min-w-0">
          <span
            class="grid size-7 shrink-0 place-items-center rounded-md bg-primary/10 text-primary"
            aria-hidden="true"
          >
            <Database className="size-4" />
          </span>
          <h2 class="truncate text-base font-bold tracking-tight">
            Ou escolha um banco de questões
          </h2>
          {phase === "ready" && (
            <span class="shrink-0 rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
              {banks.length}
            </span>
          )}
        </div>
        <p class="hidden text-xs text-muted-foreground sm:block">
          Pratique direto em um banco já curado.
        </p>
      </header>

      {phase === "loading" || phase === "idle" ? (
        <p class="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando bancos…
        </p>
      ) : phase === "error" ? (
        <p class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          Não consegui carregar os bancos agora.
        </p>
      ) : banks.length === 0 ? (
        <p class="rounded-md border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
          {q
            ? `Nenhum banco bate com a busca.`
            : "Nenhum banco disponível por aqui. Quando seu professor publicar conteúdo, aparece nessa lista."}
        </p>
      ) : (
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {banks.map((b) => (
            <BankCard key={b.id} bank={b} />
          ))}
        </div>
      )}
    </section>
  );
}

// Seção de cards agrupados por tipo (Cadernos vs Redações). Cabeçalho com
// ícone do kind, título e contagem. Quando não há nenhuma sessão do tipo,
// mostra um vazio compacto convidando a criar.
function SessionSection({
  kind,
  title,
  desc,
  sessions,
  onOpen,
  onChange,
  emptyMsg
}: {
  kind: "chat" | "essay";
  title: string;
  desc: string;
  sessions: AiStudySessionRow[];
  onOpen: (id: string) => void;
  onChange: () => Promise<void> | void;
  emptyMsg: string;
}) {
  const theme = kindTheme(kind);
  return (
    <section class="space-y-2">
      <header class="flex items-center justify-between gap-3 border-b border-border pb-2">
        <div class="flex items-center gap-2 min-w-0">
          <span
            class={`grid size-7 shrink-0 place-items-center rounded-md ${theme.iconBg}`}
            aria-hidden="true"
          >
            {kind === "essay" ? (
              <FileText className="size-4" />
            ) : (
              <MessageSquare className="size-4" />
            )}
          </span>
          <h2 class="truncate text-base font-bold tracking-tight">{title}</h2>
          <span class={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${theme.badge}`}>
            {sessions.length}
          </span>
        </div>
        <p class="hidden text-xs text-muted-foreground sm:block">{desc}</p>
      </header>
      {sessions.length === 0 ? (
        <p class="rounded-md border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
          {emptyMsg}
        </p>
      ) : (
        <ul class="grid gap-3 sm:grid-cols-2">
          {sessions.map((s) => (
            <SessionCard
              key={s.id}
              session={s}
              onOpen={() => onOpen(s.id)}
              onChange={onChange}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// Categorias usadas no fluxo "IA gera proposta". Espelham o backend
// (apps/api/src/lib/essay-proposal.ts → PROPOSAL_CATEGORIES) — quando mexer
// num lado, atualize o outro.
const PROPOSAL_CATEGORIES: Array<{ key: string; label: string; desc: string }> = [
  { key: "sociedade_cidadania", label: "Sociedade & Cidadania", desc: "Desigualdade, mobilidade, voto, convivência" },
  { key: "meio_ambiente", label: "Meio Ambiente & Sustentabilidade", desc: "Clima, consumo, biomas, energia" },
  { key: "tecnologia_digital", label: "Tecnologia & Era Digital", desc: "Redes sociais, IA, dados, privacidade" },
  { key: "saude_bem_estar", label: "Saúde & Bem-estar", desc: "Saúde mental, SUS, vacinação, hábitos" },
  { key: "educacao_cultura", label: "Educação & Cultura", desc: "Acesso, evasão, leitura" },
  { key: "politica_direitos", label: "Política & Direitos Humanos", desc: "Representatividade, liberdade, minorias" },
  { key: "trabalho_economia", label: "Trabalho & Economia", desc: "Mercado, automação, renda" },
  { key: "ciencia_pesquisa", label: "Ciência & Pesquisa", desc: "Financiamento, divulgação, ética" },
  { key: "esporte_lazer", label: "Esporte & Lazer", desc: "Acesso, indústria, juventude" },
  { key: "surpresa", label: "Surpreenda-me", desc: "IA escolhe a categoria" },
  { key: "open", label: "Tema livre (eu dou a pista)", desc: "Eu digito uma pista e a IA estrutura" }
];

type CreateEssaySource = "manual" | "ai" | "library";

function CreateSessionModal({
  onClose,
  onCreated,
  initialKind = "chat"
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  /** "chat" (caderno padrão) ou "essay" (redação). Botão "Nova redação"
   *  no header passa "essay" pra abrir já no modo certo. */
  initialKind?: "chat" | "essay";
}) {
  // Deeplink reverso banco→caderno: lê do signal, pré-popula tema e
  // pré-anexa o banco. Limpa o signal no unmount (qualquer fechamento).
  // [[project-caderno-bank-context]]
  const prefilledFromBank = pendingNewCadernoFromBank.value;
  const isEssayVersion = prefilledFromBank?.bankVersion === "essay";
  const [kind, setKind] = useState<"chat" | "essay">(initialKind);
  const [essaySource, setEssaySource] = useState<CreateEssaySource>("manual");
  const [theme, setTheme] = useState(
    prefilledFromBank
      ? isEssayVersion
        ? `Estudo discursivo do banco "${prefilledFromBank.bankName}" — versão dissertativa. A IA deve fazer perguntas abertas no formato dissertativo, avaliar suas respostas comparando com o gabarito e dar feedback por pontos esperados.`
        : `Questões e conteúdo do banco "${prefilledFromBank.bankName}"`
      : ""
  );
  const [title, setTitle] = useState(
    prefilledFromBank
      ? isEssayVersion
        ? `${prefilledFromBank.bankName} — dissertativa`.slice(0, 100)
        : prefilledFromBank.bankName.slice(0, 100)
      : ""
  );

  useEffect(() => {
    return () => {
      // Sempre limpa o signal quando o modal desmontar, pra um próximo "Novo
      // estudo" voltar ao branco. Acontece tanto no Cancelar quanto no Salvar.
      if (pendingNewCadernoFromBank.value) {
        pendingNewCadernoFromBank.value = null;
      }
    };
  }, []);
  // IA gera
  const [aiCategory, setAiCategory] = useState<string>("surpresa");
  const [aiThemeHint, setAiThemeHint] = useState("");
  const [aiFragmentCount, setAiFragmentCount] = useState<number>(3);
  const [aiBanca, setAiBanca] = useState<string>("Vestibular geral");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setErr(null);
    try {
      if (kind === "essay" && essaySource === "ai") {
        const r = await api.generateEssayProposal({
          category: aiCategory,
          themeHint: aiCategory === "open" ? aiThemeHint.trim() || null : null,
          fragmentCount: aiFragmentCount,
          banca: aiBanca
        });
        onCreated(r.id);
        return;
      }
      if (theme.trim().length < 5) {
        throw new Error(
          kind === "essay"
            ? "Tema da redação precisa ter pelo menos 5 caracteres."
            : "Tema precisa ter pelo menos 5 caracteres."
        );
      }
      const r = await api.createAiStudy({
        kind,
        theme: theme.trim(),
        title: title.trim() || undefined,
        linkedBankIds:
          prefilledFromBank && kind === "chat"
            ? [prefilledFromBank.bankId]
            : undefined
      });
      onCreated(r.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="w-full max-w-xl rounded-lg border border-border bg-card p-6 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-bold">Novo estudo com IA</h2>
          <button type="button" onClick={onClose} class="text-muted-foreground hover:text-foreground" title="Fechar">
            <X className="size-4" />
          </button>
        </div>

        <div class="space-y-3">
          {prefilledFromBank && kind === "chat" && (
            <div class="flex items-start gap-2 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2">
              <Database className="mt-0.5 size-4 shrink-0 text-violet-500" />
              <div class="min-w-0 flex-1 text-[12px] leading-snug">
                <p class="font-bold text-violet-700 dark:text-violet-300">
                  Caderno será criado com este banco anexado:
                </p>
                <p class="line-clamp-1 text-violet-800/90 dark:text-violet-200/90">
                  <strong>{prefilledFromBank.bankName}</strong>
                </p>
                <p class="mt-1 text-[10px] text-violet-700/70 dark:text-violet-300/70">
                  A IA já vai conhecer as questões e gabaritos deste banco como referência.
                </p>
              </div>
            </div>
          )}

          <div>
            <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Tipo de estudo *
            </span>
            <div class="flex flex-wrap gap-2">
              <KindSelectorCard
                active={kind === "chat"}
                onClick={() => setKind("chat")}
                kind="chat"
                title="Caderno de Estudos"
                desc="Chat com IA restrita ao tema, com upload de PDFs como apoio e histórico de conversa."
              />
              <KindSelectorCard
                active={kind === "essay"}
                onClick={() => setKind("essay")}
                kind="essay"
                title="Simulação de redação"
                desc="Escreva uma redação com tempo, fragmentos de apoio e correção da IA por banca."
              />
            </div>
          </div>

          {kind === "essay" && (
            <div>
              <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Como preparar?
              </span>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setEssaySource("manual")}
                  class={`flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    essaySource === "manual"
                      ? "border-violet-500 bg-violet-500/10"
                      : "border-border bg-background hover:border-violet-500/60"
                  }`}
                >
                  <div class="font-bold">Eu defino o tema</div>
                  <div class="text-[10px] text-muted-foreground">
                    Digito tema, banca e textos de apoio manualmente.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setEssaySource("ai")}
                  class={`flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    essaySource === "ai"
                      ? "border-violet-500 bg-violet-500/10"
                      : "border-border bg-background hover:border-violet-500/60"
                  }`}
                >
                  <div class="flex items-center gap-1 font-bold">
                    <Sparkles className="size-3.5 text-violet-500" />
                    IA gera pra mim
                  </div>
                  <div class="text-[10px] text-muted-foreground">
                    Escolho categoria + qtd de fragmentos. A IA monta tudo.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setEssaySource("library")}
                  class={`flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    essaySource === "library"
                      ? "border-violet-500 bg-violet-500/10"
                      : "border-border bg-background hover:border-violet-500/60"
                  }`}
                >
                  <div class="flex items-center gap-1 font-bold">
                    <BookOpen className="size-3.5 text-violet-500" />
                    Da biblioteca
                  </div>
                  <div class="text-[10px] text-muted-foreground">
                    Temas de provas oficiais (FUVEST, ENEM, UNICAMP…) prontos pra usar.
                  </div>
                </button>
              </div>
            </div>
          )}

          {kind === "essay" && essaySource === "library" && (
            <EssayLibraryPicker
              onPicked={(sessionId) => onCreated(sessionId)}
              onCancel={() => setEssaySource("manual")}
            />
          )}

          {kind === "essay" && essaySource === "ai" ? (
            <>
              <label class="block">
                <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Categoria
                </span>
                <select
                  value={aiCategory}
                  onChange={(e) =>
                    setAiCategory((e.target as HTMLSelectElement).value)
                  }
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                  title="Categoria do tema"
                >
                  {PROPOSAL_CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label} — {c.desc}
                    </option>
                  ))}
                </select>
              </label>
              {aiCategory === "open" && (
                <label class="block">
                  <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Pista do tema (você sugere, IA estrutura)
                  </span>
                  <input
                    type="text"
                    value={aiThemeHint}
                    onInput={(e) =>
                      setAiThemeHint((e.target as HTMLInputElement).value)
                    }
                    maxLength={200}
                    placeholder="Ex: Impacto da IA generativa na escola brasileira"
                    class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                  />
                </label>
              )}
              <div class="grid grid-cols-2 gap-2">
                <label class="block">
                  <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Banca alvo
                  </span>
                  <select
                    value={aiBanca}
                    onChange={(e) =>
                      setAiBanca((e.target as HTMLSelectElement).value)
                    }
                    class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                    title="Banca alvo"
                  >
                    <option>Vestibular geral</option>
                    <option>ENEM</option>
                    <option>FUVEST</option>
                    <option>UNICAMP</option>
                    <option>UNESP</option>
                    <option>ITA</option>
                    <option>PUC</option>
                    <option>Concurso público</option>
                  </select>
                </label>
                <label class="block">
                  <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Qtd de fragmentos
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="8"
                    value={aiFragmentCount}
                    onInput={(e) =>
                      setAiFragmentCount(
                        Math.max(0, Math.min(8, Number((e.target as HTMLInputElement).value) || 0))
                      )
                    }
                    class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                  />
                </label>
              </div>
              <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
                <Sparkles className="mr-1 inline size-3" />
                A IA pode levar 20–60 segundos pra gerar a proposta. Depois você
                ajusta tempo, linhas e configurações antes de começar a escrever.
              </div>
            </>
          ) : (
            <>
              <label class="block">
                <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {kind === "essay" ? "Tema da redação *" : "Tema do estudo *"}
                </span>
                <textarea
                  value={theme}
                  onInput={(e) => setTheme((e.target as HTMLTextAreaElement).value)}
                  placeholder={
                    kind === "essay"
                      ? "Ex: O papel das redes sociais na desinformação no Brasil contemporâneo"
                      : "Ex: Farmacologia de antibióticos beta-lactâmicos no contexto clínico"
                  }
                  rows={kind === "essay" ? 6 : 3}
                  maxLength={5000}
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
                <p class="mt-1 text-[10px] text-muted-foreground">
                  {kind === "essay"
                    ? "Tema/proposta de redação que o aluno vai dissertar (até ~5 mil chars pra caber proposta estilo ENEM). Imutável após criar."
                    : "A IA fica RESTRITA a este tema durante toda a conversa."}{" "}
                  ({theme.length}/5000)
                </p>
              </label>

              <label class="block">
                <span class="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Título (opcional)
                </span>
                <input
                  type="text"
                  value={title}
                  onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
                  placeholder="Se vazio, usa o tema"
                  maxLength={100}
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>

              {kind === "essay" && (
                <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
                  <Sparkles className="mr-1 inline size-3" />
                  Você vai configurar banca, tempo, limite e fragmentos de apoio na
                  próxima tela antes de começar a escrever.
                </div>
              )}
            </>
          )}

          {err && (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {err}
            </div>
          )}

          <div class="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void create()}
              disabled={
                busy ||
                (kind === "essay" && essaySource === "ai"
                  ? aiCategory === "open" && aiThemeHint.trim().length < 5
                  : theme.trim().length < 5)
              }
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {kind === "essay" && essaySource === "ai" ? "IA gerando…" : "Criando…"}
                </>
              ) : kind === "essay" && essaySource === "ai" ? (
                <>
                  <Sparkles className="size-4" />
                  Gerar com IA
                </>
              ) : kind === "essay" ? (
                "Criar redação"
              ) : (
                "Criar estudo"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Workspace de uma sessão
// ============================================================================

// @-mention: tipo único pra docs + notas. Origem fica no `kind` pra ícone
// e ordenação. Nota é opcional (depende do session.notes).
interface MentionItem {
  kind: "doc" | "bank" | "note";
  id: string;
  label: string;
}

function filteredMentionItems(
  data: AiStudySessionDetail | null,
  query: string
): MentionItem[] {
  if (!data) return [];
  const q = query.toLowerCase();
  const out: MentionItem[] = [];
  for (const d of data.documents ?? []) {
    if (!q || d.filename.toLowerCase().includes(q)) {
      out.push({ kind: "doc", id: d.id, label: d.filename });
    }
  }
  for (const b of data.session.linkedBanks ?? []) {
    if (!q || b.name.toLowerCase().includes(q)) {
      out.push({ kind: "bank", id: b.id, label: b.name });
    }
  }
  return out.slice(0, 8);
}

function MentionPopover({
  data,
  mention,
  onPick
}: {
  data: AiStudySessionDetail | null;
  mention: { atIdx: number; query: string; selectedIdx: number };
  onPick: (item: MentionItem) => void;
}) {
  const items = filteredMentionItems(data, mention.query);
  if (items.length === 0) {
    return (
      <div class="absolute bottom-full left-0 z-30 mb-1 rounded-md border border-border bg-popover px-3 py-2 text-[11px] italic text-muted-foreground shadow-md">
        Nenhum documento ou banco com "{mention.query}"
      </div>
    );
  }
  return (
    <div class="absolute bottom-full left-0 z-30 mb-1 min-w-[14rem] rounded-md border border-border bg-popover p-1 shadow-md">
      {items.map((it, i) => {
        const active = i === mention.selectedIdx;
        return (
          <button
            key={`${it.kind}:${it.id}`}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(it);
            }}
            class={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs ${
              active
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            <span aria-hidden="true">
              {it.kind === "doc" ? "📄" : it.kind === "bank" ? "📚" : "📝"}
            </span>
            <span class="truncate">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function AiStudyWorkspace({
  sessionId,
  onExit
}: {
  sessionId: string;
  onExit: () => void;
}) {
  const [data, setData] = useState<AiStudySessionDetail | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [openDoc, setOpenDoc] = useState<AiStudyDocument | null>(null);
  // @-mention de docs: popover ancorado no caret quando digita `@`.
  // Mantém o offset do `@` no input pra substituir corretamente ao escolher.
  const chatTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [mention, setMention] = useState<{
    atIdx: number;       // posição do `@` no input
    query: string;       // texto digitado após `@`
    selectedIdx: number; // item selecionado via teclado
  } | null>(null);

  async function load() {
    try {
      const r = await api.getAiStudy(sessionId);
      setData(r);
      activeAiStudySession.value = {
        id: r.session.id,
        title: r.session.title,
        kind: r.session.kind
      };
      setPhase("ready");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
    return () => {
      activeAiStudySession.value = null;
    };
  }, [sessionId]);

  if (data?.session.kind === "essay") {
    return <EssayWorkspace data={data} onExit={onExit} onChange={load} />;
  }

  // Insere `@"<nome>"` substituindo o `@query` que disparou o popover. O servidor
  // já tem todos os docs/notas no contexto (linked banks + uploaded docs +
  // notes via attach), então o "@" é uma dica textual pra IA focar em algo.
  function insertMention(item: MentionItem) {
    if (!mention) return;
    const before = input.slice(0, mention.atIdx);
    const after = input.slice(mention.atIdx + 1 + mention.query.length);
    const marker = `@"${item.label.replace(/"/g, "'")}" `;
    const next = before + marker + after;
    setInput(next);
    setMention(null);
    // Reposiciona o caret depois do marker
    const newCaret = (before + marker).length;
    queueMicrotask(() => {
      const ta = chatTextareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    });
  }

  async function send() {
    if (!input.trim() || sending) return;
    setSending(true);
    const msg = input.trim();
    setInput("");
    try {
      await api.sendAiStudyMessage(sessionId, msg, { verbosity: activeVerbosity() });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  if (phase === "loading" || !data) {
    return (
      <p class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando estudo…
      </p>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        {err}
      </div>
    );
  }

  return (
    <div class="flex h-[calc(100vh-10rem)] flex-col gap-3">
      <header class="flex items-start gap-3 rounded-md border border-border bg-card/40 px-3 py-2">
        {data.persona && <PersonaBadge persona={data.persona} />}
        <div class="min-w-0 flex-1">
          <button
            type="button"
            onClick={onExit}
            class="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3" /> Voltar aos estudos
          </button>
          <h1 class="mt-1 line-clamp-1 text-lg font-bold tracking-tight">{data.session.title}</h1>
          {data.persona && (
            <p class="mt-0.5 line-clamp-1 text-[11px] font-medium text-foreground">
              <Sparkles className="mr-1 inline size-3 text-violet-500" />
              Conversando com <strong>{data.persona.label}</strong>
            </p>
          )}
          <p class="line-clamp-2 text-[11px] text-muted-foreground">
            <strong>Tema:</strong> {data.session.theme}
          </p>
          {data.session.sharedByName && (
            <span class="mt-1 inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300">
              <Share2 className="size-2.5" />
              Compartilhado por <strong>{data.session.sharedByName}</strong>
            </span>
          )}
        </div>
        <SessionHeaderActions
          sessionId={sessionId}
          title={data.session.title}
          theme={data.session.theme}
          kind={data.session.kind}
          onExit={onExit}
          onRenamed={() => void load()}
        />
      </header>

      {/* Bancos anexados como contexto da IA. SEMPRE visível pro aluno saber
          o que a IA está usando como referência — e como mudar.
          [[project-caderno-bank-context]] [[feedback-user-clarity-context]] */}
      <LinkedBanksStrip
        sessionId={sessionId}
        linkedBanks={data.session.linkedBanks ?? []}
        onChange={(next: LinkedBankSummary[]) => {
          setData((prev) =>
            prev ? { ...prev, session: { ...prev.session, linkedBanks: next } } : prev
          );
        }}
      />

      {/* Simulados gerados a partir deste caderno. Não confundir com bancos
          ANEXADOS (contexto da IA, acima) — esses foram CRIADOS via
          "Gerar simulado a partir deste caderno", são objetos de estudo
          autônomos. Lista resumida com link pro detalhe do banco. */}
      {(data.generatedBanks?.length ?? 0) > 0 && (
        <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3">
          <div class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            <Sparkles className="size-3" />
            Simulados gerados deste caderno ({data.generatedBanks!.length})
          </div>
          <ul class="space-y-1.5">
            {data.generatedBanks!.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={async () => {
                    const [{ setActiveNav }, { openBank }] = await Promise.all([
                      import("./App"),
                      import("./BankDetailView")
                    ]);
                    setActiveNav("bancos");
                    openBank(b.id);
                  }}
                  class="flex w-full items-center justify-between gap-3 rounded-md border border-violet-500/20 bg-background/40 px-3 py-2 text-left transition-colors hover:bg-violet-500/10"
                >
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-sm font-semibold">
                      {b.name}
                    </span>
                    <span class="text-[10px] text-muted-foreground">
                      {b.questionCount} questões ·{" "}
                      {new Date(b.createdAt).toLocaleDateString("pt-BR")}
                    </span>
                  </span>
                  <span class="text-xs text-violet-600 dark:text-violet-300">
                    Abrir →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div class="flex min-h-0 flex-1 gap-3">
        {/* Lateral ESQUERDA — Anotações ou Documentos (depende de aiStudyNotesSide).
            Padrão "nunca os dois na mesma coluna": espelha o comportamento da
            lista de questões + IA no Modo Estudo. Cada lateral tem swap button
            próprio (que inverte o par por mutex). */}
        <CadernoSidePanel
          side="left"
          sessionId={sessionId}
          documents={data.documents}
          storage={data.storage}
          onDocChange={() => void load()}
          onOpenDoc={(d) => setOpenDoc(d)}
          openDoc={openDoc}
          onCloseDoc={() => setOpenDoc(null)}
        />

        {/* Chat */}
        <div class="flex min-w-0 flex-1 flex-col rounded-md border border-border bg-card/40">
          {(data.session.linkedBanks?.length ?? 0) > 0 && (
            <div class="flex items-center gap-2 border-b border-violet-500/20 bg-violet-500/5 px-3 py-1.5 text-[11px] text-violet-700 dark:text-violet-300">
              <Sparkles className="size-3 shrink-0 text-violet-500" />
              <span class="min-w-0 truncate">
                <strong>IA com contexto:</strong>{" "}
                {data.session.linkedBanks.map((b) => b.name).join(" · ")}
              </span>
            </div>
          )}
          <ChatMessages messages={data.messages} sending={sending} />
          <div class="border-t border-border p-2">
            <div class="flex items-end gap-2">
              <div class="relative flex-1">
                <textarea
                  ref={chatTextareaRef}
                  value={input}
                  onInput={(e) => {
                    const ta = e.target as HTMLTextAreaElement;
                    const value = ta.value;
                    setInput(value);
                    // Detecta @-mention: procura o último "@" antes do caret,
                    // sem espaço/quebra no meio. Se achar, abre o popover.
                    const caret = ta.selectionStart ?? value.length;
                    const before = value.slice(0, caret);
                    const m = before.match(/(?:^|\s)@([^\s@]*)$/);
                    if (m) {
                      const atIdx = caret - m[1].length - 1; // posição do @
                      setMention({ atIdx, query: m[1].toLowerCase(), selectedIdx: 0 });
                    } else {
                      setMention(null);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (mention) {
                      const items = filteredMentionItems(data, mention.query);
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setMention({
                          ...mention,
                          selectedIdx: Math.min(items.length - 1, mention.selectedIdx + 1)
                        });
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setMention({
                          ...mention,
                          selectedIdx: Math.max(0, mention.selectedIdx - 1)
                        });
                        return;
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setMention(null);
                        return;
                      }
                      if (e.key === "Enter" || e.key === "Tab") {
                        if (items.length > 0) {
                          e.preventDefault();
                          insertMention(items[mention.selectedIdx]);
                          return;
                        }
                      }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={
                    sending
                      ? "Aguarde a IA responder…"
                      : "Pergunte sobre o tema… (use @ pra mencionar um documento)"
                  }
                  rows={2}
                  disabled={sending}
                  class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
                />
                {mention && <MentionPopover data={data} mention={mention} onPick={insertMention} />}
              </div>
              <button
                type="button"
                onClick={() => void send()}
                disabled={sending || !input.trim()}
                class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
              >
                {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Lateral DIREITA — par mutex com a lateral esquerda. */}
        <CadernoSidePanel
          side="right"
          sessionId={sessionId}
          documents={data.documents}
          storage={data.storage}
          onDocChange={() => void load()}
          onOpenDoc={(d) => setOpenDoc(d)}
          openDoc={openDoc}
          onCloseDoc={() => setOpenDoc(null)}
        />
      </div>
    </div>
  );
}

// Painel lateral do AiStudyWorkspace — Anotações e Documentos visíveis ao
// mesmo tempo, cada um colapsável independentemente. Toggles equivalentes
// também ficam expostos no `SessionHeaderActions` pra acesso rápido.
//
// Estados persistidos via signals (compartilhados com o header). Default:
// notas abertas, docs colapsados.
export const aiStudyShowNotes = signal<boolean>(
  localStorage.getItem("aistudy.sidebar.notes") !== "0"
);
export const aiStudyShowDocs = signal<boolean>(
  localStorage.getItem("aistudy.sidebar.docs") !== "0"
);
export function toggleAiStudyNotes() {
  aiStudyShowNotes.value = !aiStudyShowNotes.value;
  try {
    localStorage.setItem("aistudy.sidebar.notes", aiStudyShowNotes.value ? "1" : "0");
  } catch {
    /* ignore */
  }
}
export function toggleAiStudyDocs() {
  aiStudyShowDocs.value = !aiStudyShowDocs.value;
  try {
    localStorage.setItem("aistudy.sidebar.docs", aiStudyShowDocs.value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

// Lado da lateral de Anotações. Documentos fica SEMPRE na lateral oposta
// (mutex automático — nunca os dois na mesma coluna, conforme padrão da
// lista de questões + IA no Modo Estudo). Persistido em localStorage.
export const aiStudyNotesSide = signal<"left" | "right">(
  localStorage.getItem("aistudy.notes.side") === "left" ? "left" : "right"
);
export function toggleAiStudyNotesSide() {
  const next = aiStudyNotesSide.value === "left" ? "right" : "left";
  aiStudyNotesSide.value = next;
  try {
    localStorage.setItem("aistudy.notes.side", next);
  } catch {
    /* ignore */
  }
}

// Largura de cada lateral em px. Drag-to-resize (padrão idêntico ao do
// Modo Estudo). Persistido por lado, então aluno pode ter "esquerda
// estreita, direita larga" ou vice-versa.
const PANEL_WIDTH_MIN = 240;
const PANEL_WIDTH_MAX = 560;
const PANEL_WIDTH_DEFAULT = 320;
function readPanelWidth(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(n)) return PANEL_WIDTH_DEFAULT;
    return Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, n));
  } catch {
    return PANEL_WIDTH_DEFAULT;
  }
}
export const aiStudyLeftPanelWidth = signal<number>(
  readPanelWidth("aistudy.panel.left.width")
);
export const aiStudyRightPanelWidth = signal<number>(
  readPanelWidth("aistudy.panel.right.width")
);
function setPanelWidth(side: "left" | "right", w: number): void {
  const clamped = Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, w));
  if (side === "left") aiStudyLeftPanelWidth.value = clamped;
  else aiStudyRightPanelWidth.value = clamped;
  try {
    localStorage.setItem(`aistudy.panel.${side}.width`, String(clamped));
  } catch {
    /* ignore */
  }
}

// Renderiza UMA lateral do AiStudyView. Decide qual painel (Anotações ou
// Documentos) baseado em aiStudyNotesSide + a posição (side). Mutex automático:
// Notes numa lateral, Docs na oposta. Cada lateral tem botão pra trocar lado
// (que inverte o par inteiro). Padrão "lista vira item": se há doc aberto na
// lateral de Docs, mostra o viewer ocupando a coluna inteira; close volta
// pra lista. [[project-caderno-3-column-layout]]
function CadernoSidePanel({
  side,
  sessionId,
  documents,
  storage,
  onDocChange,
  onOpenDoc,
  openDoc,
  onCloseDoc
}: {
  side: "left" | "right";
  sessionId: string;
  documents: AiStudyDocument[];
  storage?: { usedBytes: number; quotaBytes: number };
  onDocChange: () => void;
  onOpenDoc: (d: AiStudyDocument) => void;
  openDoc: AiStudyDocument | null;
  onCloseDoc: () => void;
}) {
  const notesSide = aiStudyNotesSide.value;
  const showNotes = aiStudyShowNotes.value;
  const showDocs = aiStudyShowDocs.value;
  const kind: "notes" | "docs" = notesSide === side ? "notes" : "docs";
  const width =
    side === "left" ? aiStudyLeftPanelWidth.value : aiStudyRightPanelWidth.value;
  const [dragging, setDragging] = useState(false);

  // Lateral oculta NÃO retorna null — fica renderizada com width=0 +
  // opacity=0, animando suave. Mesma estratégia do AiAssistant no Modo
  // Estudo. Returns null perderia a transição (DOM some/aparece de uma vez).
  const visible = (kind === "notes" && showNotes) || (kind === "docs" && showDocs);

  // Drag-to-resize (mesmo padrão do AiAssistant no Modo Estudo). O handle
  // fica na borda INTERNA — pra direita na lateral esquerda, pra esquerda
  // na lateral direita. Sinal de delta inverte conforme o lado.
  function startDrag(e: MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = side === "left"
      ? aiStudyLeftPanelWidth.value
      : aiStudyRightPanelWidth.value;
    setDragging(true);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    function onMove(ev: MouseEvent) {
      // Lateral esquerda: arrastar pra direita aumenta largura.
      // Lateral direita: arrastar pra esquerda aumenta largura.
      const delta = side === "left" ? ev.clientX - startX : startX - ev.clientX;
      setPanelWidth(side, startW + delta);
    }
    function onUp() {
      setDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const handleOnRight = side === "left";

  const transitionClass = dragging
    ? ""
    : "transition-[width,opacity,margin,border-width] duration-300 ease-out";
  const stateStyle = visible
    ? `width:${width}px;min-width:${PANEL_WIDTH_MIN}px;opacity:1`
    : `width:0px;min-width:0px;opacity:0;margin:0;pointer-events:none`;

  return (
    <aside
      class={`relative hidden shrink-0 flex-col overflow-hidden lg:flex ${transitionClass}`}
      style={stateStyle}
      aria-hidden={!visible ? "true" : "false"}
    >
      {kind === "notes" ? (
        <CadernoNotesPanel
          targetKind="ai_study_session"
          targetId={sessionId}
          onSwapSide={toggleAiStudyNotesSide}
        />
      ) : openDoc ? (
        // Doc aberto: viewer ocupa a coluna toda (lista some). Close volta
        // pra lista. Comportamento "item ocupa a coluna" pedido pelo user.
        <DocViewer doc={openDoc} onClose={onCloseDoc} />
      ) : (
        <DocsPanel
          sessionId={sessionId}
          documents={documents}
          storage={storage}
          onChange={onDocChange}
          onOpen={onOpenDoc}
          onSwapSide={toggleAiStudyNotesSide}
        />
      )}
      {/* Handle de resize — barra fininha na borda interna. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionar lateral"
        onMouseDown={startDrag}
        class={`absolute top-0 z-20 h-full w-1.5 cursor-ew-resize bg-transparent transition-colors hover:bg-primary/40 ${
          handleOnRight ? "right-0" : "left-0"
        } ${dragging ? "bg-primary/60" : ""}`}
      />
    </aside>
  );
}

function SidebarTabs({
  sessionId,
  documents,
  storage,
  onDocChange,
  onOpenDoc,
  openDoc,
  onCloseDoc
}: {
  sessionId: string;
  documents: AiStudyDocument[];
  storage?: { usedBytes: number; quotaBytes: number };
  onDocChange: () => void;
  onOpenDoc: (d: AiStudyDocument) => void;
  openDoc: AiStudyDocument | null;
  onCloseDoc: () => void;
}) {
  const showNotes = aiStudyShowNotes.value;
  const showDocs = aiStudyShowDocs.value;

  // Se ambos colapsados, mostra placeholder pra o user lembrar como reabrir.
  if (!showNotes && !showDocs) {
    return (
      <div class="flex h-full min-h-0 flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-card/20 p-6 text-center">
        <p class="text-[11px] text-muted-foreground">
          Anotações e Documentos estão escondidos.
        </p>
        <div class="flex gap-1.5">
          <button
            type="button"
            onClick={toggleAiStudyNotes}
            class="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-accent"
          >
            Mostrar anotações
          </button>
          <button
            type="button"
            onClick={toggleAiStudyDocs}
            class="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-accent"
          >
            Mostrar documentos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="flex h-full min-h-0 flex-col gap-2">
      {showNotes && (
        <div
          class={`flex min-h-0 flex-col ${
            showDocs ? "flex-[3]" : "flex-1"
          }`}
        >
          <CadernoNotesPanel
            targetKind="ai_study_session"
            targetId={sessionId}
          />
        </div>
      )}
      {showDocs && (
        <div
          class={`flex min-h-0 flex-col ${
            showNotes ? "flex-[2]" : "flex-1"
          }`}
        >
          <DocsPanel
            sessionId={sessionId}
            documents={documents}
            storage={storage}
            onChange={onDocChange}
            onOpen={onOpenDoc}
          />
          {openDoc && <DocViewer doc={openDoc} onClose={onCloseDoc} />}
        </div>
      )}
    </div>
  );
}

function ChatMessages({ messages, sending }: { messages: AiStudyMessage[]; sending: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [messages.length, sending]);

  if (messages.length === 0) {
    return (
      <div ref={ref} class="flex flex-1 items-center justify-center px-6 py-8 text-center">
        <div>
          <Sparkles className="mx-auto mb-3 size-8 text-primary" />
          <p class="text-sm text-muted-foreground">
            Comece fazendo uma pergunta sobre o tema deste estudo.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div ref={ref} class="scroll-thin flex-1 space-y-3 overflow-y-auto px-3 py-3">
      {messages.map((m) => (
        <ChatBubble key={m.id} role={m.role} content={m.content} />
      ))}
      {sending && (
        <div class="flex items-start gap-2">
          <span class="mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-sky-500/10 text-sky-600">
            <Sparkles className="size-3.5" />
          </span>
          <div class="inline-flex items-center gap-2 rounded-2xl rounded-tl-sm border border-border bg-muted/60 px-3.5 py-2.5 text-sm text-muted-foreground shadow-sm dark:bg-muted/30">
            <Loader2 className="size-3.5 animate-spin" /> Pensando…
          </div>
        </div>
      )}
    </div>
  );
}

function ChatBubble({ role, content }: { role: "user" | "assistant"; content: string }) {
  if (role === "user") {
    return (
      <div class="flex justify-end">
        <div class="max-w-[85%] rounded-2xl rounded-tr-sm border border-primary/40 bg-primary/10 px-3.5 py-2.5 text-sm leading-relaxed text-foreground shadow-sm dark:bg-primary/20">
          <pre class="whitespace-pre-wrap break-words font-sans">{content}</pre>
        </div>
      </div>
    );
  }
  return (
    <div class="flex items-start gap-2">
      <span class="mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-sky-500/10 text-sky-600">
        <Sparkles className="size-3.5" />
      </span>
      <div class="group relative min-w-0 max-w-[85%] rounded-2xl rounded-tl-sm border border-border bg-muted/60 px-3.5 py-3 pr-9 text-sm shadow-sm dark:bg-muted/30">
        <AiMarkdownBlock text={content} className="text-foreground/95" />
        <CopyButton content={content} />
      </div>
    </div>
  );
}

function CopyButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const ta = document.createElement("textarea");
        ta.value = content;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignora — sem feedback de erro
    }
  }
  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      title={copied ? "Copiado!" : "Copiar resposta"}
      aria-label={copied ? "Resposta copiada" : "Copiar resposta"}
      class={`absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-md text-muted-foreground opacity-60 transition-all hover:bg-accent hover:text-foreground hover:opacity-100 group-hover:opacity-100 ${
        copied ? "text-emerald-600 dark:text-emerald-400" : ""
      }`}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

function DocsPanel({
  sessionId,
  documents,
  storage,
  onChange,
  onOpen,
  onSwapSide
}: {
  sessionId: string;
  documents: AiStudyDocument[];
  /** Cota de armazenamento do user (passada pelo CadernoSidePanel/SidebarTabs
   *  a partir de data.storage). Pode estar undefined num refresh entre uploads —
   *  componente fallbacka pra somatório local (subestima). */
  storage?: { usedBytes: number; quotaBytes: number };
  onChange: () => void;
  onOpen: (d: AiStudyDocument) => void;
  onSwapSide?: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Storage local optimistic — atualizado pelo retorno do upload.
  const [localStorage, setLocalStorage] = useState<
    { usedBytes: number; quotaBytes: number } | null
  >(null);
  const effectiveStorage = localStorage ?? storage;
  const [urlOpen, setUrlOpen] = useState(false);
  const [citedIndex, setCitedIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Listener pra citation anchoring: quando o aluno clica num [Doc N] na
  // resposta da IA, destaca brevemente o doc na lista + abre o viewer.
  // [[project-caderno-notebooklm-roadmap]] Fase 1.
  useEffect(() => {
    function onCite(e: Event) {
      const detail = (e as CustomEvent).detail as { docIndex?: number } | undefined;
      const idx = detail?.docIndex;
      if (typeof idx !== "number") return;
      if (idx < 0 || idx >= documents.length) return;
      setCitedIndex(idx);
      // Abre o viewer do doc citado.
      onOpen(documents[idx]);
      // Reseta destaque após 2.5s pra dar feedback visual.
      const t = window.setTimeout(() => setCitedIndex(null), 2500);
      return () => window.clearTimeout(t);
    }
    document.addEventListener("caderno:cite", onCite);
    return () => document.removeEventListener("caderno:cite", onCite);
  }, [documents, onOpen]);

  async function pickFile(e: Event) {
    const target = e.target as HTMLInputElement;
    const f = target.files?.[0];
    if (!f) return;
    setUploading(true);
    setErr(null);
    try {
      const extracted = await extractTextFromFile(f);
      const res = await api.uploadAiStudyDoc(sessionId, {
        filename: f.name,
        mimeType: f.type || "application/octet-stream",
        sizeBytes: f.size,
        extractedText: extracted
      });
      if (res.storage) setLocalStorage(res.storage);
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove(docId: string) {
    const ok = await openConfirm({
      title: "Remover documento?",
      message: "O documento será removido desta sessão e a IA não terá mais acesso ao conteúdo dele.",
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteAiStudyDoc(sessionId, docId);
      onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  async function refresh(docId: string) {
    setRefreshingId(docId);
    setErr(null);
    try {
      const res = await api.refreshAiStudyUrl(sessionId, docId);
      onChange();
      const diff = res.diffBytes;
      const diffLabel =
        diff === 0
          ? "Sem mudanças"
          : diff > 0
            ? `+${(diff / 1024).toFixed(1)}KB`
            : `${(diff / 1024).toFixed(1)}KB`;
      setErr(`Snapshot atualizado · ${diffLabel}`);
      window.setTimeout(() => setErr(null), 4000);
    } catch (e) {
      const friendly = friendlyError(e);
      setErr(friendly);
    } finally {
      setRefreshingId(null);
    }
  }

  return (
    <div class="flex h-full min-h-0 flex-col rounded-md border border-border bg-card/40">
      <header class="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <FileText className="size-3.5" />
          Documentos ({documents.length})
        </div>
        <div class="flex items-center gap-1">
          {onSwapSide && (
            <button
              type="button"
              onClick={onSwapSide}
              title="Trocar lado da lateral"
              aria-label="Trocar lado"
              class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ArrowLeftRight className="size-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setUrlOpen(true)}
            class="inline-flex items-center gap-1 rounded-md border border-sky-500/30 bg-sky-500/5 px-2 py-1 text-[10px] font-semibold text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
            title="Adicionar página web como fonte"
          >
            <Globe className="size-3" />
            URL
          </button>
          <label class="cursor-pointer">
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.txt,.md,.csv"
              class="hidden"
              onChange={pickFile}
            />
            <span class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent">
              {uploading ? <Loader2 className="size-3 animate-spin" /> : <Paperclip className="size-3" />}
              Anexar
            </span>
          </label>
        </div>
      </header>
      {urlOpen && (
        <IngestUrlModal
          sessionId={sessionId}
          onClose={() => setUrlOpen(false)}
          onIngested={onChange}
        />
      )}
      {/* Barrinha de armazenamento — só mostra quando temos dados.
          [[project-caderno-storage-quota]] */}
      {effectiveStorage && (
        <StorageQuotaBar
          usedBytes={effectiveStorage.usedBytes}
          quotaBytes={effectiveStorage.quotaBytes}
        />
      )}
      {err && (
        <div class="border-b border-destructive/30 bg-destructive/5 px-3 py-1.5 text-[10px] text-destructive">
          {err}
        </div>
      )}
      <ul class="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {documents.length === 0 && (
          <li class="text-center text-[11px] text-muted-foreground">
            Sem documentos. Anexe PDF/TXT pra a IA usar como apoio.
          </li>
        )}
        {documents.map((d, i) => {
          const isUrl = d.sourceKind === "url";
          const refreshState = canRefreshNow(d);
          const isRefreshing = refreshingId === d.id;
          return (
            <li
              key={d.id}
              class={`group flex flex-col gap-1 rounded-md border bg-background px-2 py-1.5 text-xs transition-all ${
                citedIndex === i
                  ? "border-sky-500 ring-2 ring-sky-500/50"
                  : "border-border"
              }`}
            >
              <div class="flex items-center gap-2">
                <span class="inline-flex size-5 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-bold text-muted-foreground">
                  {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => onOpen(d)}
                  class="flex-1 truncate text-left font-semibold hover:underline"
                  title={d.filename}
                >
                  {isUrl && <Globe className="mr-1 inline size-3 text-sky-600 dark:text-sky-400" />}
                  {d.filename}
                </button>
                <span class="shrink-0 text-[10px] text-muted-foreground">
                  {(d.sizeBytes / 1024).toFixed(0)}KB
                </span>
                {isUrl && (
                  <button
                    type="button"
                    onClick={() => void refresh(d.id)}
                    disabled={isRefreshing || !refreshState.allowed}
                    class={`shrink-0 rounded p-1 transition-colors ${
                      isRefreshing || !refreshState.allowed
                        ? "cursor-not-allowed text-muted-foreground/40"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`}
                    title={refreshState.tooltip}
                    aria-label={refreshState.tooltip}
                  >
                    {isRefreshing ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <RefreshCw className="size-3" />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void remove(d.id)}
                  class="text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                  title="Remover"
                >
                  <Trash2 className="size-3" />
                </button>
              </div>
              {isUrl && (
                <div class="ml-7 flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span>{refreshState.statusLabel}</span>
                  {d.lastError && (
                    <span
                      class="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400"
                      title={d.lastError}
                    >
                      <AlertCircle className="size-3" /> última tentativa falhou
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p class="border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
        PDFs e TXTs são extraídos no seu navegador antes do envio.
      </p>
    </div>
  );
}

function DocViewer({ doc, onClose }: { doc: AiStudyDocument; onClose: () => void }) {
  return (
    <div class="flex min-h-0 flex-1 flex-col rounded-md border border-border bg-card/40">
      <header class="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div class="min-w-0 flex-1">
          <div class="truncate text-xs font-bold">{doc.filename}</div>
          <div class="text-[10px] text-muted-foreground">
            {doc.extractedText.length.toLocaleString("pt-BR")} caracteres extraídos
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Fechar documento"
          aria-label="Fechar documento"
          class="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>
      <pre class="flex-1 overflow-auto whitespace-pre-wrap px-3 py-2 text-[11px] leading-snug">
        {doc.extractedText.slice(0, 50_000)}
      </pre>
    </div>
  );
}

// ============================================================================
// Helpers de extração
// ============================================================================

/** Barrinha discreta mostrando "X MB / Y MB" no topo da DocsPanel.
 *  Cor muda conforme proximidade do limite: verde (<70%), âmbar (70-90%),
 *  vermelho (>90%). [[project-caderno-storage-quota]] */
function StorageQuotaBar({
  usedBytes,
  quotaBytes
}: {
  usedBytes: number;
  quotaBytes: number;
}) {
  const pct = quotaBytes > 0 ? Math.min(100, (usedBytes / quotaBytes) * 100) : 0;
  const tone =
    pct >= 90
      ? "bg-destructive"
      : pct >= 70
        ? "bg-amber-500"
        : "bg-violet-500";
  const fmt = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    const mb = n / (1024 * 1024);
    return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
  };
  return (
    <div class="border-b border-border bg-background/50 px-3 py-1.5">
      <div class="flex items-center justify-between text-[10px] text-muted-foreground">
        <span class="font-medium">Armazenamento</span>
        <span class="tabular-nums">
          {fmt(usedBytes)} / {fmt(quotaBytes)}
        </span>
      </div>
      <div class="mt-1 h-1 overflow-hidden rounded-full bg-border">
        <div
          class={`h-full ${tone} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

async function extractTextFromFile(f: File): Promise<string> {
  const lower = f.name.toLowerCase();
  if (lower.endsWith(".txt") || lower.endsWith(".md") || lower.endsWith(".csv") || f.type.startsWith("text/")) {
    return f.text();
  }
  if (lower.endsWith(".pdf") || f.type === "application/pdf") {
    return extractPdfText(f);
  }
  throw new Error(
    `Tipo não suportado ainda: ${f.type || lower.split(".").pop()}. Suportados: PDF, TXT, MD, CSV.`
  );
}

async function extractPdfText(f: File): Promise<string> {
  // Lazy-load PDF.js do CDN (já usado no resto do app pelo Editor legado)
  const w = window as any;
  if (!w.pdfjsLib) {
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.min.mjs", true);
  }
  const pdfjsLib = w.pdfjsLib;
  if (!pdfjsLib) throw new Error("Falha ao carregar PDF.js");
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs";
  const buf = await f.arrayBuffer();
  const doc = await pdfjsLib.getDocument({ data: buf }).promise;
  const out: string[] = [];
  // Pra páginas onde texto extraído é esparso (provavelmente conteúdo visual),
  // renderizamos a página como PNG e mandamos pro classifier. Cap pra não
  // explodir custo em PDFs grandes. [[project-caderno-bank-context]]
  const SPARSE_TEXT_THRESHOLD = 200;          // chars — abaixo disso = página "visual"
  const MAX_VISUAL_PAGES = 20;                // teto duro de chamadas/imagem por PDF
  const RENDER_SCALE = 1.5;                   // ~1500px em página A4
  let visualCalls = 0;
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((it: any) => (typeof it.str === "string" ? it.str : ""))
      .join(" ")
      .trim();
    out.push(pageText);

    // Página com pouco texto → tenta descrever visualmente.
    if (pageText.length < SPARSE_TEXT_THRESHOLD && visualCalls < MAX_VISUAL_PAGES) {
      try {
        const pageImageBase64 = await renderPageToBase64(page, RENDER_SCALE);
        const r = await fetch("/api/student/caderno/classify-image", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageBase64: pageImageBase64,
            mimeType: "image/png",
            filename: f.name,
            pageNumber: i
          })
        });
        visualCalls++;
        if (r.ok) {
          const data = (await r.json()) as
            | { ornament: true }
            | { description: string };
          if ("description" in data && data.description) {
            // Inline a descrição no texto da página pra IA usar como contexto.
            out[out.length - 1] =
              (pageText ? pageText + "\n\n" : "") +
              `[Página ${i} (descrição visual): ${data.description}]`;
          }
          // ornament → ignora silenciosamente.
        }
      } catch {
        // Falha de classify não bloqueia upload. Página fica só com o texto
        // que pdf.js conseguiu extrair (talvez vazio).
      }
    }

    if (out.join("\n\n").length > 280_000) break; // cap final
  }
  return out.join("\n\n");
}

/** Renderiza uma página do PDF em base64 PNG via canvas. Usado pelo
 *  pipeline de descrição visual (Onda 4). */
async function renderPageToBase64(page: any, scale: number): Promise<string> {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D não disponível");
  await page.render({ canvasContext: ctx, viewport }).promise;
  // toDataURL("image/png") retorna "data:image/png;base64,XXXX..." — tira
  // o prefixo pra mandar só o base64 cru pro backend.
  const dataUrl = canvas.toDataURL("image/png");
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function loadScript(src: string, isModule = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    if (isModule) s.type = "module";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
    document.head.appendChild(s);
  });
}

// ============================================================================
// SessionCard — card da lista com menu de ações (renomear / arquivar / apagar)
// ============================================================================

function SessionCard({
  session: s,
  onOpen,
  onChange
}: {
  session: AiStudySessionRow;
  onOpen: () => void;
  onChange: () => Promise<void> | void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState<null | "delete" | "rename">(null);
  const [err, setErr] = useState<string | null>(null);
  const isEssay = s.kind === "essay";

  async function doEditTheme(e: Event) {
    e.stopPropagation();
    setMenuOpen(false);
    const newTheme = await openPrompt({
      title: "Editar tema do caderno",
      message: "Mudar o tema altera o escopo da IA pra novas mensagens. Use entre 5 e 5000 caracteres.",
      defaultValue: s.theme,
      maxLength: 5000,
      multiline: true,
      confirmLabel: "Salvar tema",
      validate: (v) => (v.length < 5 ? "Mínimo 5 caracteres." : null)
    });
    if (!newTheme || newTheme === s.theme) return;
    setBusy("rename");
    try {
      await api.retitleAiStudyTheme(s.id, newTheme.slice(0, 5000));
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doDelete(e: Event) {
    e.stopPropagation();
    setMenuOpen(false);
    const phrase = `APAGAR ${isEssay ? "redação" : "estudo"}`;
    const typed = await openPrompt({
      title: `Apagar "${s.title}" permanentemente?`,
      message: `Isso remove TODA a sessão (mensagens, documentos, fragmentos) e NÃO pode ser desfeito.\n\nDigite "${phrase}" pra confirmar:`,
      placeholder: phrase,
      confirmLabel: "Apagar definitivamente",
      danger: true,
      requirePhrase: phrase
    });
    if (typed?.toLowerCase() !== phrase.toLowerCase()) return;
    setBusy("delete");
    try {
      await api.hardDeleteAiStudy(s.id);
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const theme = kindTheme(s.kind);
  return (
    <li
      class={`group relative cursor-pointer rounded-lg border bg-card/40 p-4 transition-colors ${theme.border} ${theme.hover}`}
      onClick={() => !menuOpen && !renaming && onOpen()}
    >
      <div class="mb-2 flex items-start gap-2">
        <Sparkles className={`mt-0.5 size-4 shrink-0 ${theme.icon}`} />
        {renaming ? (
          <RenameInput
            initialTitle={s.title}
            onCommit={async (newTitle) => {
              setRenaming(false);
              if (!newTitle || newTitle === s.title) return;
              setBusy("rename");
              try {
                await api.renameAiStudy(s.id, newTitle);
                await onChange();
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
            onCancel={() => setRenaming(false)}
          />
        ) : (
          <h3 class="flex-1 line-clamp-2 font-bold leading-snug">{s.title}</h3>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          class="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100"
          title="Ações"
          aria-label="Ações"
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <MoreVertical className="size-4" />}
        </button>
        {menuOpen && (
          <ActionMenu
            onClose={() => setMenuOpen(false)}
            onRename={(e) => {
              e.stopPropagation();
              setMenuOpen(false);
              setRenaming(true);
            }}
            onEditTheme={!isEssay ? doEditTheme : null}
            onDelete={doDelete}
          />
        )}
      </div>
      <p class="line-clamp-2 text-xs text-muted-foreground">{s.theme}</p>
      <div class="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
        <span class={`rounded-md px-1.5 py-0.5 font-bold uppercase tracking-wider ${theme.badge}`}>
          {theme.label}
        </span>
        {!isEssay && typeof s.documentsCount === "number" && s.documentsCount > 0 && (
          <span
            class="inline-flex items-center gap-1 rounded-md bg-sky-500/10 px-1.5 py-0.5 font-semibold text-sky-700 dark:text-sky-300"
            title={`${s.documentsCount} documento${s.documentsCount === 1 ? "" : "s"} anexado${s.documentsCount === 1 ? "" : "s"}`}
          >
            <Paperclip className="size-3" />
            {s.documentsCount} {s.documentsCount === 1 ? "doc" : "docs"}
          </span>
        )}
        {isEssay && s.essayPhase && (
          <span class="rounded-md bg-muted px-1.5 py-0.5 font-semibold text-muted-foreground">
            {essayPhaseLabel(s.essayPhase)}
          </span>
        )}
        {isEssay && typeof s.essayScore === "number" && (
          <span class="rounded-md bg-emerald-500/15 px-1.5 py-0.5 font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            Nota {s.essayScore.toFixed(2).replace(".", ",")}
          </span>
        )}
        {s.sharedByName && (
          <span
            class="inline-flex items-center gap-1 rounded-md border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 font-semibold text-blue-700 dark:text-blue-300"
            title={`Compartilhado por ${s.sharedByName}`}
          >
            <Share2 className="size-3" />
            por {s.sharedByName.split(" ")[0]}
          </span>
        )}
        <span class="ml-auto text-muted-foreground">
          {new Date(s.updatedAt).toLocaleDateString("pt-BR")}
        </span>
      </div>
      {err && (
        <p class="mt-2 text-[10px] text-destructive">
          <AlertCircle className="mr-1 inline size-3" /> {err}
        </p>
      )}
    </li>
  );
}

function RenameInput({
  initialTitle,
  onCommit,
  onCancel
}: {
  initialTitle: string;
  onCommit: (title: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialTitle);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <input
      ref={ref}
      type="text"
      value={value}
      maxLength={100}
      onClick={(e) => e.stopPropagation()}
      onInput={(e) => setValue((e.target as HTMLInputElement).value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(value.trim());
        else if (e.key === "Escape") onCancel();
      }}
      onBlur={() => onCommit(value.trim())}
      class="flex-1 rounded-md border border-primary bg-background px-2 py-1 text-sm font-bold outline-none"
      aria-label="Renomear estudo"
    />
  );
}

function ActionMenu({
  onClose,
  onRename,
  onEditTheme,
  onDelete
}: {
  onClose: () => void;
  onRename: (e: Event) => void;
  onEditTheme: ((e: Event) => void) | null;
  onDelete: (e: Event) => void;
}) {
  useEffect(() => {
    function handle(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-action-menu]")) onClose();
    }
    setTimeout(() => document.addEventListener("click", handle), 0);
    return () => document.removeEventListener("click", handle);
  }, []);
  return (
    <div
      data-action-menu
      class="absolute right-2 top-10 z-10 w-48 overflow-hidden rounded-md border border-border bg-card shadow-lg"
    >
      <MenuItem icon={Pencil} label="Renomear título" onClick={onRename} />
      {onEditTheme && (
        <MenuItem icon={FileText} label="Editar tema" onClick={onEditTheme} />
      )}
      <MenuItem
        icon={Trash2}
        label="Apagar (permanente)"
        onClick={onDelete}
        destructive
      />
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  destructive = false
}: {
  icon: any;
  label: string;
  onClick: (e: Event) => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold hover:bg-accent ${
        destructive ? "text-destructive hover:bg-destructive/10" : ""
      }`}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

// ============================================================================
// SessionHeaderActions — botão de ações usado no header dos workspaces
// (chat e essay). Reusa as mesmas confirmações do card da lista.
// ============================================================================

export function SessionHeaderActions({
  sessionId,
  title,
  theme,
  kind,
  onExit,
  onRenamed
}: {
  sessionId: string;
  title: string;
  theme: string;
  kind: "chat" | "essay";
  onExit: () => void;
  onRenamed: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState<null | "rename" | "delete">(null);
  const [err, setErr] = useState<string | null>(null);

  async function doRename() {
    setMenuOpen(false);
    const newTitle = await openPrompt({
      title: "Renomear",
      message: "Digite o novo título da sessão.",
      defaultValue: title,
      maxLength: 100,
      confirmLabel: "Salvar"
    });
    if (!newTitle || newTitle === title) return;
    setBusy("rename");
    try {
      await api.renameAiStudy(sessionId, newTitle.slice(0, 100));
      onRenamed();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doEditTheme(currentTheme: string) {
    setMenuOpen(false);
    const newTheme = await openPrompt({
      title: "Editar tema do caderno",
      message: "Mudar o tema altera o escopo da IA pra novas mensagens. Use entre 5 e 5000 caracteres.",
      defaultValue: currentTheme,
      maxLength: 5000,
      multiline: true,
      confirmLabel: "Salvar tema",
      validate: (v) => (v.length < 5 ? "Mínimo 5 caracteres." : null)
    });
    if (!newTheme || newTheme === currentTheme) return;
    setBusy("rename");
    try {
      await api.retitleAiStudyTheme(sessionId, newTheme.slice(0, 5000));
      onRenamed();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doDelete() {
    setMenuOpen(false);
    const phrase = `APAGAR ${kind === "essay" ? "redação" : "estudo"}`;
    const typed = await openPrompt({
      title: `Apagar "${title}" permanentemente?`,
      message: `Isso remove TODA a sessão (mensagens, documentos, fragmentos) e NÃO pode ser desfeito.\n\nDigite "${phrase}" pra confirmar:`,
      placeholder: phrase,
      confirmLabel: "Apagar definitivamente",
      danger: true,
      requirePhrase: phrase
    });
    if (typed?.toLowerCase() !== phrase.toLowerCase()) return;
    setBusy("delete");
    try {
      await api.hardDeleteAiStudy(sessionId);
      onExit();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  const showNotes = aiStudyShowNotes.value;
  const showDocs = aiStudyShowDocs.value;
  const [genOpen, setGenOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);

  return (
    <div class="flex shrink-0 items-center gap-1.5">
      {/* Gerar simulado — só pra caderno (chat). Esconde em redação.
          [[project-caderno-bank-context]] */}
      {kind === "chat" && (
        <button
          type="button"
          onClick={() => setGenOpen(true)}
          title="Gerar banco de questões a partir deste caderno"
          aria-label="Gerar simulado a partir deste caderno"
          class="inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/10 px-2 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
        >
          <Wand2 className="size-3.5" />
          <span class="hidden md:inline">Gerar simulado</span>
        </button>
      )}
      {genOpen && (
        <GenerateBankModal
          sessionId={sessionId}
          sessionTitle={title}
          onClose={() => setGenOpen(false)}
        />
      )}
      {/* Análises do caderno (briefing/summary/glossary/study_guide/faq + audio).
          [[project-caderno-notebooklm-roadmap]] Fase 1 */}
      {kind === "chat" && (
        <button
          type="button"
          onClick={() => setAnalysisOpen(true)}
          title="Gerar análises a partir das fontes do caderno"
          aria-label="Análises do caderno"
          class="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] font-bold text-sky-700 hover:bg-sky-500/15 dark:text-sky-300"
        >
          <Sparkles className="size-3.5" />
          <span class="hidden md:inline">Análises</span>
        </button>
      )}
      {analysisOpen && (
        <CadernoAnalysisModal
          sessionId={sessionId}
          sessionTitle={title}
          hasDocuments={true}
          onClose={() => setAnalysisOpen(false)}
        />
      )}
      {/* Toggles do painel lateral — visíveis no bloco de título do
          workspace, padrão equivalente ao Modo Estudo (lista de questões +
          IA). */}
      <button
        type="button"
        onClick={toggleAiStudyNotes}
        title={showNotes ? "Esconder anotações" : "Mostrar anotações"}
        aria-label="Mostrar/esconder anotações"
        aria-pressed={showNotes}
        class={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors ${
          showNotes
            ? "border-primary/40 bg-primary/10 text-primary"
            : "border-border bg-background text-muted-foreground hover:bg-accent"
        }`}
      >
        <StickyNote className="size-3.5" />
        <span class="hidden md:inline">Anotações</span>
      </button>
      <button
        type="button"
        onClick={toggleAiStudyDocs}
        title={showDocs ? "Esconder documentos" : "Mostrar documentos"}
        aria-label="Mostrar/esconder documentos"
        aria-pressed={showDocs}
        class={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors ${
          showDocs
            ? "border-primary/40 bg-primary/10 text-primary"
            : "border-border bg-background text-muted-foreground hover:bg-accent"
        }`}
      >
        <FileText className="size-3.5" />
        <span class="hidden md:inline">Documentos</span>
      </button>
      <div class="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        disabled={busy !== null}
        title="Ações da sessão"
        aria-label="Ações da sessão"
      >
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <MoreVertical className="size-3.5" />}
      </button>
      {menuOpen && (
        <div
          data-action-menu
          class="absolute right-0 top-9 z-10 w-48 overflow-hidden rounded-md border border-border bg-card shadow-lg"
        >
          <MenuItem icon={Pencil} label="Renomear título" onClick={() => void doRename()} />
          {kind === "chat" && (
            <MenuItem
              icon={FileText}
              label="Editar tema"
              onClick={() => void doEditTheme(theme)}
            />
          )}
          {flagEnabled("student.menu.shares", false) && (
            <MenuItem
              icon={Share2}
              label="Compartilhar"
              onClick={() => {
                setMenuOpen(false);
                openShareDialog({
                  kind: "caderno",
                  resourceId: sessionId,
                  resourceTitle: title
                });
              }}
            />
          )}
          <MenuItem
            icon={Trash2}
            label="Apagar (permanente)"
            onClick={() => void doDelete()}
            destructive
          />
        </div>
      )}
      {err && (
        <div class="absolute right-0 top-9 z-20 w-64 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[10px] text-destructive">
          {err}
        </div>
      )}
      </div>
    </div>
  );
}

// ============================================================================
// KindSelectorCard — botão de seleção do tipo no modal de criação. Usa a
// paleta de kindTheme pra cada um (Caderno azul / Redação violet).
// ============================================================================

function KindSelectorCard({
  active,
  onClick,
  kind,
  title,
  desc
}: {
  active: boolean;
  onClick: () => void;
  kind: "chat" | "essay";
  title: string;
  desc: string;
}) {
  const theme = kindTheme(kind);
  const cls = active
    ? `${theme.selectedBorder} ${theme.selectedBg}`
    : `border-border bg-background ${theme.hover}`;
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex-1 rounded-md border px-3 py-2 text-left text-sm transition-colors ${cls}`}
    >
      <div class="mb-1 flex items-center gap-1.5">
        <Sparkles className={`size-3.5 ${theme.icon}`} />
        <span class="font-bold">{title}</span>
      </div>
      <div class="text-[10px] text-muted-foreground">{desc}</div>
    </button>
  );
}

// Calcula se a fonte URL pode receber refresh manual agora.
// Política (negociada com user 2026-05-20):
//   - 1 sucesso por 24h sobre lastSnapshotAt.
//   - Se a última tentativa falhou (lastError preenchido), libera retry.
//   - statusLabel descreve "capturado há Xh" pra mostrar inline.
const COOLDOWN_MS = 24 * 60 * 60 * 1000;
function canRefreshNow(d: AiStudyDocument): {
  allowed: boolean;
  tooltip: string;
  statusLabel: string;
} {
  if (d.sourceKind !== "url") {
    return { allowed: false, tooltip: "Apenas fontes URL", statusLabel: "" };
  }
  const snap = d.lastSnapshotAt ? new Date(d.lastSnapshotAt).getTime() : 0;
  const now = Date.now();
  const ageMs = snap > 0 ? now - snap : Infinity;
  const sinceLabel =
    ageMs === Infinity
      ? "Capturado: nunca"
      : ageMs < 60_000
        ? "Capturado agora"
        : ageMs < 3600_000
          ? `Capturado há ${Math.floor(ageMs / 60_000)}min`
          : ageMs < 86_400_000
            ? `Capturado há ${Math.floor(ageMs / 3600_000)}h`
            : `Capturado há ${Math.floor(ageMs / 86_400_000)}d`;

  // Última tentativa falhou → libera retry sem esperar.
  if (d.lastError) {
    return {
      allowed: true,
      tooltip: "Tentar capturar de novo (última falhou)",
      statusLabel: sinceLabel
    };
  }
  if (ageMs >= COOLDOWN_MS || snap === 0) {
    return {
      allowed: true,
      tooltip: "Atualizar snapshot agora",
      statusLabel: sinceLabel
    };
  }
  const remainMs = COOLDOWN_MS - ageMs;
  const remainLabel =
    remainMs < 3600_000
      ? `${Math.ceil(remainMs / 60_000)}min`
      : `${Math.ceil(remainMs / 3600_000)}h`;
  return {
    allowed: false,
    tooltip: `Atualizado há pouco. Libera em ${remainLabel}.`,
    statusLabel: `${sinceLabel} · próximo refresh em ${remainLabel}`
  };
}

// =============================================================================
// ChooseCadernoForBankModal — modal de escolha quando aluno clica "Estudar
// com IA" dentro de um banco. Pergunta se quer adicionar a um caderno
// existente OU criar um novo. Evita que aluno crie 1 caderno por banco.
// [[project-ai-study-add-bank-to-existing]]
// =============================================================================

function ChooseCadernoForBankModal({
  bankInfo,
  existingChats,
  onClose,
  onAttached,
  onChooseNew
}: {
  bankInfo: { bankId: string; bankName: string; bankVersion?: "mc" | "essay" };
  existingChats: AiStudySessionRow[];
  onClose: () => void;
  onAttached: (sessionId: string) => void;
  onChooseNew: () => void;
}) {
  const [attachingTo, setAttachingTo] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const filtered = q.trim()
    ? existingChats.filter((s) =>
        `${s.title} ${s.theme ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())
      )
    : existingChats;

  async function addToExisting(sessionId: string) {
    setAttachingTo(sessionId);
    setErr(null);
    try {
      const r = await api.addAiStudyBank(sessionId, bankInfo.bankId);
      if (r.alreadyAttached) {
        // banco já estava lá — só abre o caderno (não é erro)
      }
      onAttached(sessionId);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setAttachingTo(null);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        class="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <div class="min-w-0 flex-1">
            <h2 class="flex items-center gap-2 text-base font-bold">
              <Sparkles className="size-5 text-violet-600 dark:text-violet-300" />
              Estudar com IA
            </h2>
            <p class="mt-0.5 truncate text-xs text-muted-foreground">
              Banco: <strong>{bankInfo.bankName}</strong>
              {bankInfo.bankVersion === "essay" && " · versão dissertativa"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-3 overflow-y-auto p-4">
          {existingChats.length > 0 && (
            <>
              <div>
                <h3 class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Adicionar a um caderno existente
                </h3>
                <p class="text-[11px] text-muted-foreground">
                  Recomendado quando você já está estudando o tema. A IA passa
                  a ter esse banco como referência além do que já tinha.
                </p>
              </div>
              {existingChats.length > 5 && (
                <input
                  type="search"
                  value={q}
                  onInput={(e) => setQ((e.target as HTMLInputElement).value)}
                  placeholder="Buscar caderno por título ou tema…"
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                />
              )}
              <ul class="space-y-1.5">
                {filtered.length === 0 ? (
                  <li class="rounded-md border border-dashed border-border px-3 py-3 text-center text-[11px] italic text-muted-foreground">
                    Nenhum caderno bate com a busca.
                  </li>
                ) : (
                  filtered.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => void addToExisting(s.id)}
                        disabled={attachingTo !== null}
                        class="flex w-full items-center gap-3 rounded-md border border-border bg-background px-3 py-2 text-left transition-colors hover:border-violet-500/50 hover:bg-violet-500/5 disabled:opacity-50"
                      >
                        <div class="grid size-9 shrink-0 place-items-center rounded-md bg-violet-500/10 text-violet-700 dark:text-violet-300">
                          <MessageSquare className="size-4" />
                        </div>
                        <div class="min-w-0 flex-1">
                          <div class="truncate text-sm font-semibold">{s.title}</div>
                          {s.theme && (
                            <div class="truncate text-[11px] text-muted-foreground">
                              {s.theme}
                            </div>
                          )}
                        </div>
                        {attachingTo === s.id ? (
                          <Loader2 className="size-4 animate-spin text-violet-600" />
                        ) : (
                          <Plus className="size-4 text-muted-foreground" />
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
              {err && (
                <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  {err}
                </div>
              )}

              <div class="flex items-center gap-2 pt-2">
                <div class="h-px flex-1 bg-border" />
                <span class="text-[10px] uppercase tracking-wider text-muted-foreground">ou</span>
                <div class="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={onChooseNew}
            disabled={attachingTo !== null}
            class="flex w-full items-center gap-3 rounded-md border border-violet-500/40 bg-violet-500/5 px-3 py-2.5 text-left transition-colors hover:border-violet-500/60 hover:bg-violet-500/10 disabled:opacity-50"
          >
            <div class="grid size-9 shrink-0 place-items-center rounded-md bg-violet-500/15 text-violet-700 dark:text-violet-300">
              <Plus className="size-5" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-bold text-violet-800 dark:text-violet-200">
                Criar novo caderno
              </div>
              <div class="text-[11px] text-muted-foreground">
                Útil pra um tema novo. Você define o tema/título e a IA fica
                restrita a ele desde o começo.
              </div>
            </div>
            <ArrowRightIcon className="size-4 text-violet-600 dark:text-violet-300" />
          </button>
        </div>

        <div class="flex shrink-0 items-center justify-end border-t border-border bg-card px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={attachingTo !== null}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// Wrapper local pro ícone — lucide-react já tem ArrowRight, mas a nomenclatura
// pode conflitar; usa import explícito.
function ArrowRightIcon(props: { className?: string }) {
  return (
    <svg
      class={props.className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
